import { useEffect, useRef, useState, type DragEvent } from 'react';
import { toast } from 'sonner';

import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import {
  MAX_PRESCRIPTION_IMAGE_BYTES,
  prescriptionApi,
  UPLOAD_ERROR,
  type UploadedImageKey,
} from '@/entities/prescription/api/prescription.api';
import {
  useInvalidatePrescriptionQueries,
  usePresignedUrls,
  useUploadComplete,
} from '@/entities/prescription/hooks/usePrescriptionImages';
import {
  convertPdfToJpegPages,
  isPdfFile,
} from '@/entities/prescription/lib/pdfToJpegPages';
import { getUploadSettlementYearMonth } from '@/entities/prescription/lib/uploadSettlementMonth';
import { getApiErrorCode } from '@/shared/api/http-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Icon } from '@/shared/components/ui/icon';
import { SheetFooter } from '@/shared/components/ui/sheet';
import { cn } from '@/shared/lib/utils';
import { formatFileSize, formatYm } from '@/shared/utils/format';
import { toastApi } from '@/shared/utils/toast';
import type { PrescriptionUploadTarget } from '@/stores/uiStore';

/**
 * 업로드 조건 (M-06): JPG · PNG · PDF, 건당 10MB, 1회 최대 20장.
 * 10MB는 서버도 완료 때 막는다(PRESCRIPTION_IMAGE_TOO_LARGE). 형식 · 개수는 웹만 막는다 (C1)
 */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const MAX_FILE_SIZE = MAX_PRESCRIPTION_IMAGE_BYTES;
const MAX_FILES = 20;
/** S3 PUT 동시 업로드 수 */
const UPLOAD_CONCURRENCY = 3;
/** 20장을 넘겨 고르면 넘친 파일은 넣지 않고 이 토스트를 띄운다 (Figma 905:13883) */
const MAX_FILES_MESSAGE = `처방 내역 등록은 최대 ${MAX_FILES}장까지 가능해요.`;

type QueueStatus =
  'converting' | 'ready' | 'invalid' | 'uploading' | 'done' | 'failed';

interface QueueItem {
  id: number;
  file: File;
  status: QueueStatus;
  /** invalid일 때 사유 */
  error?: string;
  /** uploading일 때 0~100 */
  progress: number;
  /** converting일 때 PDF 페이지 변환 진행 */
  convert?: { done: number; total: number };
}

const getValidationError = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXTENSIONS.includes(ext))
    return 'JPG · PNG · PDF 파일만 등록할 수 있어요.';
  if (file.size > MAX_FILE_SIZE)
    return '해당 파일이 10MB를 넘어 등록할 수 없어요.';
  return undefined;
};

/** 20장 한도에 세는 행 — 등록 대기 · 실패(재시도 대상) · 올리는 중 */
const countAccepted = (list: QueueItem[]) =>
  list.filter(
    (i) =>
      i.status === 'ready' || i.status === 'failed' || i.status === 'uploading',
  ).length;

/** 행 왼쪽 상태 아이콘 — 대기 · 완료 초록 체크 / 올리는 중 빈 원 / 실패 · 오류 빨간 ✕ */
const StatusIcon = ({ status }: { status: QueueStatus }) => {
  if (status === 'ready' || status === 'done')
    return (
      <Icon
        name="check-circle-solid-24"
        className="text-green-500"
        label={status === 'done' ? '등록 완료' : '등록 대기'}
      />
    );
  if (status === 'invalid' || status === 'failed')
    return (
      <Icon
        name="delete-circle-solid-24"
        className="text-red-500"
        label={status === 'failed' ? '등록 실패' : '등록할 수 없는 파일'}
      />
    );
  return <Icon name="circle-line-24" className="text-grey-300" />;
};

/**
 * 업로드 목록의 한 행 (Figma list 905:13596 · 905:13606 · 905:13615) — 회색 r20 · 안쪽 16.
 * 아이콘 + 파일명(16 Medium) [+ 진행 막대 300×6 / 오류 사유] · 크기(14) · [삭제] · 실패면 [재시도]
 */
const QueueRow = ({
  item,
  disabled,
  onRemove,
  onRetry,
}: {
  item: QueueItem;
  disabled: boolean;
  onRemove: () => void;
  onRetry: () => void;
}) => {
  const isError = item.status === 'invalid' || item.status === 'failed';
  const inProgress =
    item.status === 'uploading' || item.status === 'converting';
  const progress =
    item.status === 'uploading'
      ? item.progress
      : item.convert && item.convert.total > 0
        ? Math.round((item.convert.done / item.convert.total) * 100)
        : 0;
  return (
    <li className="flex items-center gap-2 rounded-20 bg-surface-grey p-4">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <StatusIcon status={item.status} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <p
              className={cn(
                'truncate text-body-16-m',
                isError ? 'text-fg-error' : 'text-fg-primary',
              )}
            >
              {item.file.name}
            </p>
            {item.status === 'invalid' && (
              <p className="text-body-16-m text-fg-error">{item.error}</p>
            )}
          </div>
          {inProgress && (
            <div
              className="h-1.5 w-full max-w-[300px] overflow-hidden rounded-[3px] bg-grey-200"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={
                item.status === 'converting'
                  ? 'PDF를 페이지별 이미지로 바꾸는 중'
                  : '업로드 중'
              }
            >
              <div
                className="h-full rounded-[3px] bg-brand-500 transition-[width]"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
      <span className="shrink-0 text-right text-body-14-r text-fg-tertiary">
        {item.status === 'converting' ? '' : formatFileSize(item.file.size)}
      </span>
      {item.status !== 'done' && (
        <Button
          variant="text"
          className="px-1"
          disabled={disabled || item.status === 'uploading'}
          onClick={onRemove}
        >
          삭제
        </Button>
      )}
      {item.status === 'failed' && (
        <Button
          variant="textPrimary"
          className="px-1"
          disabled={disabled}
          onClick={onRetry}
        >
          재시도
        </Button>
      )}
    </li>
  );
};

/**
 * M-06 처방 내역 등록 모달 (Figma 처방등록 848:8755 — 정상 905:13745 · 로딩/오류 905:13711 · 20장 초과 905:13883)
 * + M-07 등록 중 · 일부 실패 · 완료, 중단 확인 팝업(856:10002)
 * 업로드 순서: presigned-urls → 파일별 S3 PUT(동시 3개, 진행률) → 성공한 파일만 upload-complete
 * v1.0: 실패한 파일을 한 번에 다시 올리는 [전체 재업로드]를 둔다.
 */
export const PrescriptionUploadDialog = ({
  target,
  onClose,
}: {
  target: PrescriptionUploadTarget;
  onClose: () => void;
}) => {
  // 업로드 전 안내용 — 모달을 연 시점 기준으로 고정한다. 완료 뒤에는 서버 응답 값을 쓴다
  const [expectedSettlementYm] = useState(() => getUploadSettlementYearMonth());
  /** 마지막 완료 응답의 정산월 — 완료 토스트에 쓴다 */
  const registeredSettlementYm = useRef<string | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState({ done: 0, total: 0 });
  const [dragging, setDragging] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const nextId = useRef(1);
  /** 이번 모달에서 등록 완료된 누적 장수 (재시도 포함) */
  const registeredCount = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // "병원" 줄의 종별 — 목록 캐시에서 찾는다 (실적 화면에서 열어도 같은 쿼리를 쓴다)
  const clinicTypeLabel = useBusinessRelationshipList().data?.items.find(
    (i) => i.relationshipId === target.relationshipId,
  )?.clinicTypeLabel;

  const presignedUrls = usePresignedUrls();
  const uploadComplete = useUploadComplete();
  const invalidate = useInvalidatePrescriptionQueries();

  const readyCount = items.filter((i) => i.status === 'ready').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const convertingCount = items.filter((i) => i.status === 'converting').length;
  /** 중단하면 사라지는 장수 — 오류 건은 어차피 등록되지 않으므로 세지 않는다 */
  const pendingCount = readyCount + failedCount;

  // 등록 중에는 새로고침 · 탭 닫기를 브라우저 이탈 경고로 막는다
  useEffect(() => {
    if (!uploading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  const patchItems = (ids: number[], patch: Partial<QueueItem>) =>
    setItems((prev) =>
      prev.map((i) => (ids.includes(i.id) ? { ...i, ...patch } : i)),
    );

  /**
   * 검증해서 목록 항목으로 만든다. 형식 · 크기 오류는 사유와 함께 행으로 남기고,
   * 20장 한도를 넘친 파일은 넣지 않는다 (넘친 수를 돌려준다)
   */
  const toQueueItems = (files: File[], acceptedSoFar: number) => {
    let accepted = acceptedSoFar;
    let overflow = 0;
    const result: QueueItem[] = [];
    files.forEach((file) => {
      const error = getValidationError(file);
      if (!error) {
        if (accepted >= MAX_FILES) {
          overflow += 1;
          return;
        }
        accepted += 1;
      }
      result.push({
        id: nextId.current++,
        file,
        status: error ? 'invalid' : 'ready',
        error,
        progress: 0,
      });
    });
    return { result, overflow };
  };

  /**
   * 파일 추가 — PDF는 앱과 같이 페이지별 JPEG로 바꿔 넣는다 (원본 PDF는 올리지 않음).
   * 변환하는 동안은 PDF 자리에 진행 행을 두고, 끝나면 페이지 이미지 행들로 바꾼다.
   */
  const addFiles = (files: FileList | File[] | null) => {
    if (!files || uploading) return;
    const list = Array.from(files);
    if (list.length === 0) return;

    if (countAccepted(items) >= MAX_FILES) {
      toastApi.error(MAX_FILES_MESSAGE);
      return;
    }

    const pdfs = list.filter(isPdfFile);
    const images = list.filter((f) => !isPdfFile(f));
    const placeholders = pdfs.map((file): QueueItem => ({
      id: nextId.current++,
      file,
      status: 'converting',
      progress: 0,
      convert: { done: 0, total: 0 },
    }));
    const { result, overflow } = toQueueItems(images, countAccepted(items));
    if (overflow > 0) toastApi.error(MAX_FILES_MESSAGE);
    setItems((prev) => [...prev, ...result, ...placeholders]);

    placeholders.forEach((ph) => {
      convertPdfToJpegPages(ph.file, (done, total) =>
        patchItems([ph.id], { convert: { done, total } }),
      )
        .then((pages) =>
          setItems((prev) => {
            const at = prev.findIndex((i) => i.id === ph.id);
            if (at < 0) return prev; // 변환 중에 지운 경우
            const rest = prev.filter((i) => i.id !== ph.id);
            const converted = toQueueItems(pages, countAccepted(rest));
            if (converted.overflow > 0)
              window.setTimeout(() => toastApi.error(MAX_FILES_MESSAGE), 0);
            return [
              ...prev.slice(0, at),
              ...converted.result,
              ...prev.slice(at + 1),
            ];
          }),
        )
        .catch(() =>
          patchItems([ph.id], {
            status: 'invalid',
            error: 'PDF를 이미지로 바꾸지 못했어요. 파일을 확인해 주세요.',
            convert: undefined,
          }),
        );
    });
  };

  /** 완료 토스트 (Figma 848:9980) — 제목 + 정산월 안내 */
  const finishWithToast = () => {
    const ym = registeredSettlementYm.current ?? expectedSettlementYm;
    const ymLabel = `${ym.slice(0, 4)}. ${ym.slice(4, 6)}`;
    const viewing = target.viewingSettlementYearMonth;
    // 실적 화면에서 보던 달과 다른 정산월로 들어갔으면 어디로 갔는지 알려 준다
    const elsewhere =
      viewing && viewing !== ym
        ? ` 보고 있는 ${formatYm(viewing)}이 아닌 ${formatYm(ym)} 실적에서 확인할 수 있어요.`
        : '';
    toast.success('처방 내역이 등록되었어요.', {
      description: `${ymLabel} 정산분으로 자동 분류됐어요.${elsewhere}`,
      duration: (elsewhere ? 5 : 3) * 1000,
    });
    onClose();
  };

  const runUpload = async (targets: QueueItem[]) => {
    if (targets.length === 0 || uploading) return;
    const ids = targets.map((t) => t.id);
    setUploading(true);
    setUploadStep({ done: 0, total: targets.length });
    patchItems(ids, { status: 'uploading', progress: 0 });

    const succeeded: Array<{ id: number } & UploadedImageKey> = [];
    let hasFailure = false;

    try {
      const urls = await presignedUrls.mutateAsync({
        relationshipId: target.relationshipId,
        filenames: targets.map((t) => t.file.name),
      });
      // 응답은 요청한 filenames 순서와 같다. 같은 이름이 여러 개일 수 있어 순서로 맞춘다
      let cursor = 0;
      const worker = async () => {
        while (cursor < targets.length) {
          const index = cursor++;
          const item = targets[index];
          const url = urls[index];
          try {
            if (!url) throw new Error('presigned URL 없음');
            await prescriptionApi.uploadToS3(url.presignedUrl, item.file, (p) =>
              patchItems([item.id], { progress: Math.min(p, 99) }),
            );
            succeeded.push({
              id: item.id,
              shortKey: url.shortKey,
              uploadKey: url.uploadKey,
              uploadToken: url.uploadToken,
            });
          } catch {
            hasFailure = true;
            patchItems([item.id], { status: 'failed', progress: 0 });
          } finally {
            setUploadStep((s) => ({ ...s, done: s.done + 1 }));
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(UPLOAD_CONCURRENCY, targets.length) },
          worker,
        ),
      );

      if (succeeded.length > 0) {
        const succeededIds = succeeded.map((s) => s.id);
        try {
          // 발급 응답의 세 값을 그대로 보낸다 (하나라도 바뀌면 400)
          const result = await uploadComplete.mutateAsync({
            relationshipId: target.relationshipId,
            images: succeeded.map(({ shortKey, uploadKey, uploadToken }) => ({
              shortKey,
              uploadKey,
              uploadToken,
            })),
          });
          registeredCount.current += succeeded.length;
          registeredSettlementYm.current = result.settlementYearMonth;
          patchItems(succeededIds, { status: 'done', progress: 100 });
          void invalidate();
        } catch (error) {
          // 전부 저장되지 않는다 — 다시 올리면 새 키로 발급부터 한다
          hasFailure = true;
          if (getApiErrorCode(error) === UPLOAD_ERROR.TOO_LARGE) {
            // 서버가 올린 파일을 지웠다. 10MB 넘는 파일을 가려낼 수 없으므로 전부 다시 고르게 한다
            patchItems(succeededIds, {
              status: 'invalid',
              error: '10MB를 넘는 이미지가 있어 등록하지 못했어요.',
              progress: 0,
            });
          } else {
            patchItems(succeededIds, { status: 'failed', progress: 0 });
          }
        }
      }
    } catch {
      hasFailure = true;
      patchItems(ids, { status: 'failed', progress: 0 });
    } finally {
      setUploading(false);
    }

    // 실패분이 없으면 닫고 토스트 (오류 건은 제외하고 등록한 것으로 본다)
    if (!hasFailure && registeredCount.current > 0) finishWithToast();
  };

  const startUpload = () =>
    runUpload(items.filter((i) => i.status === 'ready'));
  /** [전체 재업로드] — 실패한 파일을 한 번에 다시 올린다 */
  const retryAll = () => runUpload(items.filter((i) => i.status === 'failed'));

  /** ✕ · ESC · 배경 · 취소 — 등록 중에는 막고, 대기 목록이 있으면 중단 확인 */
  const requestClose = () => {
    if (uploading) return;
    if (pendingCount > 0) {
      setConfirmClose(true);
      return;
    }
    if (registeredCount.current > 0) finishWithToast();
    else onClose();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && requestClose()}>
        <DialogContent
          hideClose
          className="lg:max-h-[90vh] lg:max-w-[640px] lg:rounded-20"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-10 pb-4 lg:px-10">
            {/* 제목 · 병원 · 정산 월 (Figma left 905:13546) */}
            <div className="flex flex-col gap-4">
              <DialogTitle className="text-heading-20-b text-fg-primary">
                처방 내역 등록
              </DialogTitle>
              <dl className="flex flex-col gap-2 text-body-16-m">
                <div className="flex items-center gap-2">
                  <dt className="w-12 shrink-0 text-fg-tertiary">병원</dt>
                  <dd className="min-w-0 text-body-16-sb break-keep text-fg-primary">
                    {target.clinicName}
                    {clinicTypeLabel && ` · ${clinicTypeLabel}`}
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="w-12 shrink-0 text-fg-tertiary">정산 월</dt>
                  <dd className="text-body-16-sb text-fg-primary">
                    {formatYm(expectedSettlementYm)}
                  </dd>
                </div>
              </dl>
            </div>
            <DialogDescription className="sr-only">
              올린 처방전 이미지는 등록한 날짜에 따라 정해지는 정산 월로 자동
              분류돼요.
            </DialogDescription>

            {/* 드롭존 (Figma dropzone 905:13555) */}
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                if (!uploading) setDragging(true);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setDragging(false);
              }}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center gap-4 rounded-16 border border-dashed px-4 py-6 text-center transition-colors',
                dragging
                  ? 'border-brand-500 bg-brand-75'
                  : 'border-line-dark bg-surface-grey',
              )}
            >
              <div className="flex flex-col items-center gap-1">
                <p className="text-body-16-sb text-fg-primary">
                  <span className="hidden lg:inline">
                    처방전 이미지를 여기로 끌어다 놓으세요
                  </span>
                  <span className="lg:hidden">
                    처방전 이미지를 선택해 주세요
                  </span>
                </p>
                <p className="text-body-14-r text-fg-secondary">
                  JPG · PNG · PDF · 건당 10MB 이하 · 여러 장 동시 업로드 가능
                </p>
              </div>
              <Button
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                파일 선택
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className="hidden"
                data-testid="prescription-file-input"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            {items.length > 0 && (
              <section className="flex flex-col gap-2" aria-label="업로드 목록">
                {failedCount > 0 && !uploading && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-body-16-m text-fg-error" role="alert">
                      {failedCount}장이 등록되지 않았어요. 연결을 확인하고 다시
                      시도해주세요.
                    </p>
                    <Button variant="text" className="px-1" onClick={retryAll}>
                      전체 재업로드
                    </Button>
                  </div>
                )}
                <ul className="flex flex-col gap-2">
                  {items.map((item) => (
                    <QueueRow
                      key={item.id}
                      item={item}
                      disabled={uploading}
                      onRemove={() =>
                        setItems((prev) => prev.filter((i) => i.id !== item.id))
                      }
                      onRetry={() => runUpload([item])}
                    />
                  ))}
                </ul>
              </section>
            )}
          </div>

          {/* 푸터 고정 (Bottom Sheet Footer Item 905:13670) */}
          <SheetFooter className="border-t border-line-grey">
            <Button
              variant="grey"
              size="giant"
              disabled={uploading}
              onClick={requestClose}
            >
              취소
            </Button>
            <Button
              size="giant"
              disabled={!uploading && (readyCount === 0 || convertingCount > 0)}
              loading={uploading}
              onClick={startUpload}
            >
              {uploading
                ? `등록 중 ${Math.min(uploadStep.done + 1, uploadStep.total)}/${uploadStep.total}`
                : readyCount > 0
                  ? `${readyCount}장 등록하기`
                  : '등록하기'}
            </Button>
          </SheetFooter>
          <Button
            variant="text"
            size="medium"
            onClick={requestClose}
            disabled={uploading}
            aria-label="닫기"
            className="absolute top-2 right-2"
            leftIcon={<Icon name="cancel-line-24" />}
          />
        </DialogContent>
      </Dialog>

      {/* M-07 중단 확인 팝업 (Figma 856:10002) */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogTitle>등록을 중단할까요?</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {`업로드 대기 중인 이미지 ${pendingCount}장이 사라져요.\n이미 등록한 이미지는 그대로 남아요.`}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                if (registeredCount.current > 0) finishWithToast();
                else onClose();
              }}
            >
              중단하기
            </AlertDialogCancel>
            <AlertDialogAction>계속 등록하기</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
